import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators, AbstractControl } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OnboardingService } from '../../../services/onboarding/onboarding.service';
import { UtilityService } from '../../../services/utility/utility.service';
import { FieldValidators } from '../../shared/validators/field-validators';
import { FieldErrorComponent } from '../../shared/components/field-error/field-error.component';
import { isFieldInvalid,validateFile } from '../../shared/validators/form-validation.utils';

@Component({
  selector: 'app-da-experience',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    InputTextareaModule,
    DialogModule,
    SelectButtonModule,
    ButtonModule,
    ToastModule,
    DropdownModule,
    CalendarModule,
    FieldErrorComponent
  ],
  providers: [MessageService],
  templateUrl: './experience.component.html',
  styleUrl: './experience.component.scss'
})
export class DaExperienceComponent implements OnInit {
  @Input() parentForm!: FormGroup;
@Output() loaded = new EventEmitter<void>();
  displayPreviewDialog: boolean = false;
  safePdfUrl: SafeResourceUrl | null = null;
  previewFileName: string = '';
  originalFileUrl: string = '';
  isPdfLoading: boolean = false;
  isSavingMap: { [key: number]: boolean } = {};
  isSavingNoExperience: boolean = false;
  hasNoExperienceRecordId: number | null = null;

  // Delete dialog state
  showDeleteDialog = false;
  isDeleting = false;
  pendingDeleteIndex: number | null = null;
  yesNoOptions = [{ label: 'Yes', value: true }, { label: 'No', value: false }];
  countryOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private onboardingService: OnboardingService,
    private utilityService: UtilityService,
    private sanitizer: DomSanitizer,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    if (!this.parentForm.get('hasExperience')) {
      this.parentForm.addControl('hasExperience', this.fb.control(null, Validators.required));
    }
    if (!this.parentForm.get('experience')) {
      this.parentForm.addControl('experience', this.fb.array([]));
    }

    this.parentForm.get('hasExperience')?.valueChanges.subscribe(val => {
      if (val) {
        this.experience.enable();
        if (this.experience.length === 0) {
          this.addExperience();
        }
      } else {
        this.experience.disable();
         this.experience.clear();
      }
      // Do NOT markAsPristine here — we need dirty tracked so Save button warning works
    });

    this.loadCountries();
    this.loadExperiencesData();
  }

  loadCountries() {
    this.utilityService.getCountries().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.result || []);
        this.countryOptions = list.map((c: any) => ({ label: c.name, value: c.name }));
      },
      error: () => { this.countryOptions = []; }
    });
  }

  get experience(): FormArray {
    return this.parentForm.get('experience') as FormArray;
  }

  loadExperiencesData() {
    this.onboardingService.getExperiences().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.result || []);
        if (list && list.length > 0) {
          // Separate real entries from the "No" placeholder
          const realEntries = list.filter((r: any) => r.hasExperienceDetails !== false);
          const noPlaceholder = list.find((r: any) => r.hasExperienceDetails === false);

          if (realEntries.length > 0) {
            // User has real experiences — show Yes and load them
            this.parentForm.get('hasExperience')?.setValue(true);
            this.experience.clear();
            realEntries.forEach((record: any) => {
              this.addExperience(record);
            });
            this.hasNoExperienceRecordId = null;
          } else if (noPlaceholder) {
            // Only a "No" placeholder exists
            this.parentForm.get('hasExperience')?.setValue(false);
            this.experience.clear();
            this.hasNoExperienceRecordId = noPlaceholder.id ?? noPlaceholder.Id;
          } else {
            this.parentForm.get('hasExperience')?.setValue(false);
            this.experience.clear();
            this.hasNoExperienceRecordId = null;
          }
          this.parentForm.get('hasExperience')?.markAsPristine();
          this.experience.markAsPristine();
        } else {
          this.parentForm.get('hasExperience')?.setValue(false);
          this.parentForm.get('hasExperience')?.markAsPristine();
          this.hasNoExperienceRecordId = null;
        }
         this.loaded.emit();
      },
      error: (err) => {
        if (this.experience.length === 0) {
          this.addExperience();
        }
        this.loaded.emit();
      }
    });
  }

  addExperience(data?: any) {
    const group = this.fb.group({
      id: [data?.id || null],
      organisation: [data?.organisationName || '', FieldValidators.alphanumericText],
      designation: [data?.designation || '', FieldValidators.alphanumericText],
      city: [data?.city || '', FieldValidators.cityName],
      country: [data?.country || '', FieldValidators.requiredOnly],
      durationFrom: [data?.from ? new Date(data.from) : null, FieldValidators.requiredOnly],
      durationTo: [data?.to ? new Date(data.to) : null, FieldValidators.requiredOnly],
      responsibilities: [data?.keyResponsibilities || '', FieldValidators.description500],
      fileUrl: [data?.experienceOrCVLetterUrl || null]
    });
    this.experience.push(group);
  }

  saveNoExperience() {
    const hasExpControl = this.parentForm.get('hasExperience');
    if (!hasExpControl?.dirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      return;
    }

    this.isSavingNoExperience = true;
    const formData = new FormData();
    formData.append('HasExperienceDetails', 'false');

    this.onboardingService.createExperience(formData).subscribe({
      next: (res: any) => {
        const newId = res?.id || res?.Id || res?.result?.id || res?.result?.Id;
        if (newId) {
          this.hasNoExperienceRecordId = newId;
        }
        hasExpControl.markAsPristine();
        this.isSavingNoExperience = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Experience preference saved successfully.' });
      },
      error: () => {
        hasExpControl.markAsPristine();
        this.isSavingNoExperience = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Experience preference saved successfully.' });
      }
    });
  }

  removeExperience(index: number) {
    const id = this.experience.at(index).get('id')?.value;
    if (!id) {
      this.experience.removeAt(index);
      return;
    }
    this.pendingDeleteIndex = index;
    this.showDeleteDialog = true;
  }

  confirmDelete() {
    if (this.pendingDeleteIndex === null) return;
    const index = this.pendingDeleteIndex;
    const id = this.experience.at(index).get('id')?.value;
    this.isDeleting = true;
    this.onboardingService.deleteExperience(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        this.experience.removeAt(index);
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Experience entry removed.' });
      },
      error: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete experience entry.' });
      }
    });
  }

  cancelDelete() {
    this.showDeleteDialog = false;
    this.pendingDeleteIndex = null;
  }

  saveExperience(index: number) {
    const expGroup = this.experience.at(index) as FormGroup;
    expGroup.markAllAsTouched();
    if (expGroup.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
      this.scrollToFirstError(index);
      return;
    }

    const val = expGroup.value;
    const existingId = val.id;

    const formData = new FormData();
    if (existingId) {
      formData.append('Id', existingId.toString());
    }

    formData.append('OrganisationName', val.organisation || '');
    formData.append('Designation', val.designation || '');
    formData.append('City', val.city || '');
    formData.append('Country', val.country || '');

    if (val.durationFrom) {
      formData.append('From', typeof val.durationFrom === 'object' ? val.durationFrom.toISOString() : val.durationFrom);
    }
    if (val.durationTo) {
      formData.append('To', typeof val.durationTo === 'object' ? val.durationTo.toISOString() : val.durationTo);
    }
    formData.append('KeyResponsibilities', val.responsibilities || '');
    formData.append('HasExperienceDetails', 'true');

    const selectedFile = (expGroup as any)._selectedFile;
    if (selectedFile) {
      formData.append('ExperienceOrCVLetterUrl', selectedFile);
    }

    if (existingId) {
      // Dirty check: skip update if nothing changed
      if (!expGroup.dirty && !selectedFile) {
        this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
        return;
      }
      this.isSavingMap[index] = true;
      this.onboardingService.updateExperience(existingId, formData).subscribe({
        next: (res: any) => {
          this.isSavingMap[index] = false;
          this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Experience record updated successfully.' });
          expGroup.markAsPristine();
          this.parentForm.get('hasExperience')?.markAsPristine();
        },
        error: (err) => {
          this.isSavingMap[index] = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update experience record.' });
        }
      });
    } else {
      this.isSavingMap[index] = true;
      this.onboardingService.createExperience(formData).subscribe({
        next: (res: any) => {
          this.isSavingMap[index] = false;
          const newId = res?.id || res?.result?.id;
          if (newId) {
            expGroup.get('id')?.setValue(newId);
          }
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Experience record saved successfully.' });
          expGroup.markAsPristine();
          this.parentForm.get('hasExperience')?.markAsPristine();
        },
        error: (err) => {
  this.isSavingMap[index] = false;
  const backendMsg = err?.error?.errors?.documentUrl?.[0] || 'Failed to update award.';
  this.messageService.add({ severity: 'error', summary: 'Error', detail: backendMsg });
}
      });
    }
  }

onFileSelected(event: any, index: number, inputRef: HTMLInputElement) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  const check = validateFile(file);
  if (!check.valid) {
    this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
    inputRef.value = '';
    return;
  }

  const expGroup = this.experience.at(index) as FormGroup;
  (expGroup as any)._selectedFile = file;
  const localUrl = URL.createObjectURL(file);
  expGroup.get('fileUrl')?.setValue(localUrl);
  expGroup.get('fileUrl')?.markAsDirty();
  inputRef.value = '';
}

  getFileName(url: string | null): string {
    if (!url) return 'No file uploaded';
    try {
      const decoded = decodeURIComponent(url);
      return decoded.split('/').pop()?.split('?')[0] || 'Document';
    } catch {
      return 'Document';
    }
  }

  previewFile(url: string, name: string = 'Document Preview') {
    if (!url) return;
    this.originalFileUrl = url;
    this.previewFileName = name;
    this.isPdfLoading = true;
    // Blob URLs are local (file selected but not saved yet) — open directly
    // Remote URLs route through Google Docs Viewer
    let viewerUrl: string;
    if (url.startsWith('blob:')) {
      viewerUrl = url;
    } else {
      viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    }
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    this.displayPreviewDialog = true;
  }

  formatDuration(dateStr: string): string {
    if (!dateStr) return '';
    // If it's already a short year or word, return it
    if (dateStr.length < 10) return dateStr;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
  }

  readonly isFieldInvalid = isFieldInvalid;

  private scrollToFirstError(index: number) {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid'
      ];
      // Scope the query to the specific experience item by its formGroupName index
      const expItems = document.querySelectorAll('.exp-item');
      const container = expItems[index] as HTMLElement | undefined;
      const root = container ?? document;
      const el = root.querySelector(selectors.join(', '));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }
}
